import React, {
  useCallback,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import EditableTable, {
  EditableTableColumn,
  EditableTableRef,
} from "./common/EditableTable";
import { EditableTableConfig } from "./common/types";
import {
  PPATariff,
  PPATariffCreate,
  PPATariffUpdate,
  ValidationEntry,
} from "../../types";
import { formatNumber } from "../../utils/formatters";
import { colors } from "../../styles/colors";

interface PPATableProps {
  /** Liste der PPA-Verträge */
  ppaTariffs: PPATariff[];
  /** Ob Bearbeitung erlaubt ist */
  canEdit: boolean;
  /** Projektlaufzeit in Jahren (für Dropdown-Optionen) */
  projektLaufzeit: number;
  /** Validierungseinträge aus der API (für Warning-Anzeige) */
  validationEntries?: ValidationEntry[];
  /** Callback zum Erstellen eines PPA */
  onCreatePPA: (data: PPATariffCreate) => Promise<PPATariff>;
  /** Callback zum Aktualisieren eines PPA */
  onUpdatePPA: (tariffId: string, data: PPATariffUpdate) => Promise<PPATariff>;
  /** Callback zum Löschen eines PPA */
  onDeletePPA: (tariffId: string) => Promise<void>;
}

export interface PPATableRef {
  /** Startet den Erstellungsmodus mit Default-Werten */
  startCreating: (defaults?: Partial<PPATariff>) => void;
}

/**
 *
 * Wiederverwendbare Tabellen-Komponente für PPA-Verträge.
 * Unterstützt CRUD-Operationen und zeigt API-basierte Validierungs-Warnings an.
 */
const PPATable = forwardRef<PPATableRef, PPATableProps>(
  (
    {
      ppaTariffs,
      canEdit,
      projektLaufzeit,
      validationEntries = [],
      onCreatePPA,
      onUpdatePPA,
      onDeletePPA,
    },
    ref,
  ) => {
    const tableRef = useRef<EditableTableRef>(null);

    const safePPATariffs = Array.isArray(ppaTariffs) ? ppaTariffs : [];

    // Abnahmemenge für die Anzeige in MWh/Jahr umrechnen (API liefert kWh/Jahr)
    const displayPPATariffs = useMemo(
      () =>
        safePPATariffs.map((ppa) => ({
          ...ppa,
          abnahmemenge_kwh_jahr: ppa.abnahmemenge_kwh_jahr / 1000,
        })),
      [safePPATariffs],
    );

    /**
     * Erstellt eine Map von PPA-ID zu Validierungseinträgen für schnellen Lookup.
     * Gruppiert Validierungen nach PPA für die Inline-Anzeige in der Tabelle.
     */
    const validationsByPPA = useMemo(() => {
      const map = new Map<string, ValidationEntry[]>();

      validationEntries.forEach((entry) => {
        // Nur PPA-Tarif Validierungen berücksichtigen
        if (entry.entity.type === "ppa_tariff") {
          const existing = map.get(entry.entity.id) ?? [];
          map.set(entry.entity.id, [...existing, entry]);
        }
      });

      return map;
    }, [validationEntries]);

    /**
     * Holt den Tooltip-Text für einen PPA basierend auf API-Validierungen.
     * Unterscheidet zwischen verschiedenen Validierungscodes.
     */
    const getWarningTooltipForPPA = useCallback(
      (
        ppaId: string,
        field?: "beginn" | "ende" | "abnahmemenge",
      ): string | null => {
        const ppaValidations = validationsByPPA.get(ppaId);
        if (!ppaValidations || ppaValidations.length === 0) return null;

        // Filtere nach relevanten Codes basierend auf dem Feld
        let relevantValidations = ppaValidations;

        if (field === "beginn") {
          relevantValidations = ppaValidations.filter(
            (v) => v.code === "PPA_BEGINS_AFTER_PROJECT_RUNTIME",
          );
        } else if (field === "ende") {
          relevantValidations = ppaValidations.filter(
            (v) => v.code === "PPA_EXCEEDS_PROJECT_RUNTIME",
          );
        }

        if (relevantValidations.length === 0) return null;

        // Nimm die erste relevante Validierung für den Tooltip
        return relevantValidations[0].message;
      },
      [validationsByPPA],
    );

    // Helper: Generiert Options für Jahr-Dropdowns
    const generateYearOptions = (von: number, bis: number) => {
      const options: { value: string; label: string }[] = [];
      for (let i = von; i <= bis; i++) {
        options.push({ value: String(i), label: `Jahr ${i}` });
      }
      return options;
    };

    // Spalten-Definition für PPA-Tabelle
    const columns: EditableTableColumn[] = useMemo(
      () => [
        {
          key: "bezeichnung",
          label: "Bezeichnung",
          align: "left",
          width: "30%",
          sortable: true,
          editConfig: {
            type: "text",
            required: true,
          },
        },
        {
          key: "beginn_jahr",
          label: "Beginn (Jahr)",
          align: "right",
          width: "15%",
          sortable: true,
          editConfig: {
            type: "dropdown",
            required: true,
            getOptions: (row: PPATariff) => {
              // Beginn: 1 bis Ende (wenn eingegeben), sonst bis Projektlaufzeit
              const endeEingegeben =
                row.ende_jahr !== undefined &&
                row.ende_jahr !== null &&
                row.ende_jahr > 0;
              const maxJahr = endeEingegeben ? row.ende_jahr : projektLaufzeit;
              return generateYearOptions(1, maxJahr);
            },
          },
        },
        {
          key: "ende_jahr",
          label: "Ende (Jahr)",
          align: "right",
          width: "15%",
          sortable: true,
          editConfig: {
            type: "dropdown",
            required: true,
            getOptions: (row: PPATariff) => {
              // Ende: Beginn bis Projektlaufzeit
              const minJahr = row.beginn_jahr || 1;
              return generateYearOptions(minJahr, projektLaufzeit);
            },
          },
        },
        {
          key: "preis_kwh",
          label: "Preis (Cent/kWh)",
          align: "right",
          width: "20%",
          sortable: true,
          editConfig: {
            type: "number",
            suffix: "Ct/kWh",
            required: true,
            allowDecimals: true,
            min: 0,
          },
        },
        {
          key: "abnahmemenge_kwh_jahr",
          label: "Abnahme (MWh/Jahr)",
          align: "right",
          width: "20%",
          sortable: true,
          editConfig: {
            type: "number",
            suffix: "MWh/Jahr",
            required: true,
            allowDecimals: true,
            min: 0,
          },
        },
      ],
      [projektLaufzeit],
    );

    // Edit-Konfiguration für PPA-Tabelle
    const editConfig: EditableTableConfig<PPATariff> = useMemo(
      () => ({
        fields: {
          bezeichnung: { type: "text", required: true },
          beginn_jahr: { type: "dropdown", required: true },
          ende_jahr: { type: "dropdown", required: true },
          preis_kwh: {
            type: "number",
            suffix: "Ct/kWh",
            required: true,
            allowDecimals: true,
            min: 0,
          },
          abnahmemenge_kwh_jahr: {
            type: "number",
            suffix: "MWh/Jahr",
            required: true,
            allowDecimals: true,
            min: 0,
          },
        },
        onCreate: async (data: Partial<PPATariff>) => {
          // Konvertiere String-Werte von Dropdowns zu Zahlen
          // Abnahmemenge: UI zeigt MWh/Jahr → API erwartet kWh/Jahr (×1000)
          const ppaData: PPATariffCreate = {
            bezeichnung: data.bezeichnung || "",
            beginn_jahr:
              typeof data.beginn_jahr === "string"
                ? parseInt(data.beginn_jahr, 10)
                : data.beginn_jahr || 1,
            ende_jahr:
              typeof data.ende_jahr === "string"
                ? parseInt(data.ende_jahr, 10)
                : data.ende_jahr || 1,
            preis_kwh: data.preis_kwh || 0,
            abnahmemenge_kwh_jahr: (data.abnahmemenge_kwh_jahr || 0) * 1000,
          };
          return await onCreatePPA(ppaData);
        },
        onUpdate: async (ppaId: string, changes: Partial<PPATariff>) => {
          const updateData: PPATariffUpdate = {};
          if (changes.bezeichnung !== undefined)
            updateData.bezeichnung = changes.bezeichnung;
          // Konvertiere String-Werte von Dropdowns zu Zahlen
          if (changes.beginn_jahr !== undefined) {
            updateData.beginn_jahr =
              typeof changes.beginn_jahr === "string"
                ? parseInt(changes.beginn_jahr, 10)
                : changes.beginn_jahr;
          }
          if (changes.ende_jahr !== undefined) {
            updateData.ende_jahr =
              typeof changes.ende_jahr === "string"
                ? parseInt(changes.ende_jahr, 10)
                : changes.ende_jahr;
          }
          if (changes.preis_kwh !== undefined)
            updateData.preis_kwh = changes.preis_kwh;
          if (changes.abnahmemenge_kwh_jahr !== undefined)
            updateData.abnahmemenge_kwh_jahr =
              changes.abnahmemenge_kwh_jahr * 1000;
          return await onUpdatePPA(ppaId, updateData);
        },
        onDelete: async (ppaId: string) => {
          await onDeletePPA(ppaId);
        },
        validateRow: (data: Partial<PPATariff>) => {
          const errors: Record<string, string> = {};

          // Cross-Field-Validierung: ende_jahr >= beginn_jahr
          const beginn =
            typeof data.beginn_jahr === "string"
              ? parseInt(data.beginn_jahr, 10)
              : (data.beginn_jahr ?? 0);
          const ende =
            typeof data.ende_jahr === "string"
              ? parseInt(data.ende_jahr, 10)
              : (data.ende_jahr ?? 0);
          if (ende < beginn && beginn > 0) {
            errors.ende_jahr = "Muss >= Beginn sein";
          }

          // Preis muss > 0 sein
          if (!data.preis_kwh || data.preis_kwh <= 0) {
            errors.preis_kwh = "Muss > 0 sein";
          }

          // Abnahmemenge muss > 0 sein
          if (!data.abnahmemenge_kwh_jahr || data.abnahmemenge_kwh_jahr <= 0) {
            errors.abnahmemenge_kwh_jahr = "Muss > 0 sein";
          }

          return errors;
        },
      }),
      [onCreatePPA, onUpdatePPA, onDeletePPA],
    );

    // Custom Cell-Renderer für Formatierung
    // Verwendet API-basierte Validierungen für Warning-Anzeige
    const renderCell = useCallback(
      (column: EditableTableColumn, row: PPATariff, value: any) => {
        switch (column.key) {
          case "bezeichnung":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {value}
              </Typography>
            );

          case "beginn_jahr": {
            const beginnWarningTooltip = getWarningTooltipForPPA(
              row.id,
              "beginn",
            );
            const hasWarning = !!beginnWarningTooltip;

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.5,
                }}
              >
                {hasWarning && (
                  <Tooltip title={beginnWarningTooltip} arrow placement="top">
                    <WarningAmberIcon
                      sx={{
                        color: colors.state.highlight,
                        fontSize: "1.1rem",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  sx={[
                    {
                      fontWeight: "medium",
                    },
                    hasWarning && { color: colors.state.highlightDark },
                  ]}
                >
                  Jahr {value}
                </Typography>
              </Box>
            );
          }

          case "ende_jahr": {
            const endeWarningTooltip = getWarningTooltipForPPA(row.id, "ende");
            const hasWarning = !!endeWarningTooltip;

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.5,
                }}
              >
                {hasWarning && (
                  <Tooltip title={endeWarningTooltip} arrow placement="top">
                    <WarningAmberIcon
                      sx={{
                        color: colors.state.highlight,
                        fontSize: "1.1rem",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  sx={[
                    {
                      fontWeight: "medium",
                    },
                    hasWarning && { color: colors.state.highlightDark },
                  ]}
                >
                  Jahr {value}
                </Typography>
              </Box>
            );
          }

          case "preis_kwh":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {formatNumber(value, 2)}Ct/kWh
              </Typography>
            );

          case "abnahmemenge_kwh_jahr": {
            const warningTooltip = getWarningTooltipForPPA(
              row.id,
              "abnahmemenge",
            );
            const hasWarning = !!warningTooltip;

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.5,
                }}
              >
                {hasWarning && (
                  <Tooltip title={warningTooltip} arrow placement="top">
                    <WarningAmberIcon
                      sx={{
                        color: colors.state.highlight,
                        fontSize: "1.1rem",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  sx={[
                    {
                      fontWeight: "medium",
                    },
                    hasWarning && { color: colors.state.highlightDark },
                  ]}
                >
                  {formatNumber(value, 0)} MWh/Jahr
                </Typography>
              </Box>
            );
          }

          default:
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {value}
              </Typography>
            );
        }
      },
      [getWarningTooltipForPPA],
    );

    const handleAddPPA = useCallback(() => {
      // Finde das höchste Ende der bestehenden PPAs
      const hoechstesEnde = safePPATariffs.reduce(
        (max, ppa) => Math.max(max, ppa.ende_jahr || 0),
        0,
      );

      // Neuer Beginn = höchstes Ende + 1 (mindestens 1, maximal Projektlaufzeit)
      const neuerBeginn = Math.min(
        Math.max(hoechstesEnde + 1, 1),
        projektLaufzeit,
      );

      // Neues Ende = Beginn + 3 (maximal Projektlaufzeit)
      const neuesEnde = Math.min(neuerBeginn + 3, projektLaufzeit);

      tableRef.current?.startCreating({
        bezeichnung: "",
        beginn_jahr: neuerBeginn,
        ende_jahr: neuesEnde,
        preis_kwh: 0,
        abnahmemenge_kwh_jahr: 0,
      });
    }, [safePPATariffs, projektLaufzeit]);

    // Exponiere die startCreating-Methode über ref
    useImperativeHandle(
      ref,
      () => ({
        startCreating: (defaults?: Partial<PPATariff>) => {
          if (defaults) {
            tableRef.current?.startCreating(defaults);
          } else {
            handleAddPPA();
          }
        },
      }),
      [handleAddPPA],
    );

    return (
      <EditableTable<PPATariff>
        ref={tableRef}
        columns={columns}
        data={displayPPATariffs}
        editConfig={editConfig}
        canEdit={canEdit}
        renderCell={renderCell}
        actionsColumnWidth="80px"
        actionsColumnAlign="right"
        variant="default"
        showBorder={false}
        emptyMessage="Es wurden noch keine PPA-Verträge erfasst"
        defaultSortColumn="beginn_jahr"
        defaultSortDirection="asc"
      />
    );
  },
);

PPATable.displayName = "PPATable";

export default PPATable;
